/* otf-address-fields.js - 19 Sep 2026, Bu Site.

   The delivery-address block in the order sheet: Area, Street, Jadda, House,
   Floor/Flat, in the Kuwait shape, gift-aware, with the GOVERNORATE DERIVED
   FROM THE AREA and never asked. Full history and design:
   oct20 obsidian/oct20/Bu Site/Address Capture.md and
   _workspace/05_bu-site_address-prefill-BUILD.md.

   ### THREE THINGS THAT MUST NOT BE UNDONE ###

   1. NO BLOCK FIELD HERE. There was one; it was removed 17 Sep on purpose.
      Shopify's Kuwait checkout has its own REQUIRED Block box that NOTHING can
      pre-fill - no district/block/extendedFields member exists in
      CartDeliveryAddressInput, MailingAddressInput or CartSelectableAddressInput
      on ANY Storefront API version through unstable, none in the Admin API, and
      a draft order renders the same empty box. Shopify writes that box INTO
      address2 on submit and never reads address2 back INTO it - a one-way
      field. A Block field here meant the customer typed it TWICE, which is what
      the CEO objected to. Do not add it back, and do not add a "your block is 8"
      hint - there is no block on this sheet to hint at.

   2. THE SAVED ADDRESS BOOK IS OFFERED, NEVER PRE-FILLED, and never offered at
      all on a gift. This shop runs NEW_CUSTOMER_ACCOUNTS, which saves every
      completed checkout address to the buyer's book with no merchant setting to
      stop it - so the book fills with OTHER PEOPLE'S addresses. Order #2253 is
      the bug in one order: the shipping address stayed on a hospital ward the
      buyer had sent flowers to nine days earlier.

   3. LOAD ORDER. This file must load AFTER otf-address-skip.js. The last
      wrapper installed on window.fetch runs FIRST, so loading this one last
      means the attributes are merged before address-skip inspects the body.
      It attaches from OUTSIDE fluid-motion.js (106 KB, deliberately not
      edited), like fluid-rv-sheet.js and otf-slot-cutoff.js before it.

   ### CHANGED 19 Sep 2026 - the Area field, and only the Area field ###

   The CEO: "make it a proper searchable dropdown and look up all the areas in
   kuwait and make sure to have all of them."

   (a) THE AREA TABLE IS NOW COMPLETE: 167 areas, all six governorates,
       reconciled from EN Wikipedia "Areas of Kuwait" and AR Wikipedia
       "قائمة مناطق الكويت". PACI's own site was unreachable from the build
       session, so two independent published lists were reconciled instead;
       every row traces to at least one of them and nothing was invented.
       Counts: Asimah 44, Hawalli 20, Farwaniyah 27, Ahmadi 32, Jahra 30,
       Mubarak Al-Kabeer 14. Table and sources:
       _workspace/06_kuwait-areas.csv and 07_bu-site_area-dropdown-BUILD.md.

       ONE LIVE ERROR CORRECTED: Anjafa / أنجفة was filed under Al Ahmadi. It is
       HAWALLI - it sits between Bidaa and Salmiya. Both sources agree.

       Every row now carries ALIASES - the spellings people actually type
       (Naseem/Nuseem/Nasseem, Salmiya/Salmiyah, Mishref/Mishrif,
       Rumaithiya/Rumaithiyah, Abu Halifa/Abu Hulaifa, Mangaf/Mangef,
       Jleeb/Jaleeb). Umm Al Hayman / ام الهيمان is an alias of Ali Sabah Al
       Salem because two real orders (#2244, #2246) used it as the address.
       Aliases resolve the governorate exactly like real names, which is what
       makes the six-governorate fallback rare.

   (b) THE NATIVE <datalist> IS GONE, replaced by a real ARIA combobox. The
       datalist was weak where it mattered: on iPhone Safari it renders as a
       cramped native strip, it cannot be driven from the keyboard predictably,
       and it matches only the ONE string in the option value - so "nuseem" or
       "abu hulaifa" matched nothing and fell through to the governorate select.
       The replacement filters over English + Arabic + aliases at once, ranks
       prefix above substring, and lists results in a real listbox.

       THE LIST IS INLINE, NOT FLOATED, deliberately. A floated list needs an
       opaque background, which would mean inventing a colour that is not in the
       palette; and on iOS the on-screen keyboard pushes floated lists
       off-screen. Inline needs neither. Everything is currentColor / inherit /
       transparent, exactly like .otf-addr-pick.

   ARABIC SEARCH folds ال, ة/ه, ى/ي, أإآ and the diacritics, so "الرميثية",
   "رميثيه" and "الرميثيه" all find the same area. The site is MSA - the
   Kuwaiti dialect is for social only.
*/
(function () {
  'use strict';
  if (window.__otfAddrFields) return;
  window.__otfAddrFields = true;

  var AR = (document.documentElement.lang || '').indexOf('ar') === 0;
  /* 5 Oct 2026: the Arabic column now speaks Kuwaiti plural, like the rest of
     the order sheet (CEO rule, 31 Aug). It was the one formal-Arabic holdout. */
  var T = {
    head:     ['Delivery address', 'عنوان التوصيل'],
    yours:    ['Where should we deliver it', 'وين نوصّله'],
    theirs:   ['Where does the recipient live', 'وين ساكن اللي بيستلم'],
    area:     ['Area', 'المنطقة'],
    street:   ['Street', 'الشارع'],
    jadda:    ['Jadda (if the area has one)', 'الجادة (إذا فيه)'],
    house:    ['House / building', 'البيت أو العمارة'],
    floor:    ['Floor / flat (optional)', 'الدور أو الشقة (اختياري)'],
    gov:      ['Governorate', 'المحافظة'],
    govauto:  ['Governorate', 'المحافظة'],
    govpick:  ['We do not know that area. Choose the governorate.', 'ما عرفنا هالمنطقة. اختاروا المحافظة.'],
    err:      ['Add the area, the street and the house number so the driver can find it.',
               'حطوا المنطقة والشارع ورقم البيت عشان السائق يقدر يوصل.'],
    saved:    ['Deliver to', 'التوصيل إلى'],
    other:    ['A different address', 'عنوان ثاني'],
    note:     ['Kuwait has no postcodes. Checkout asks for your block on the next screen — everything else is already filled in.',
               'ما في رموز بريدية بالكويت. صفحة الدفع بتطلب رقم القطعة بالخطوة الجاية، والباقي معبّى.'],
    callnote: ['We will call them and take the address. Nothing to fill in here.',
               'إحنا نتصل فيهم وناخذ العنوان. ما تحتاجون تعبّون شي هني.'],
    phL:      ['Start typing your area', 'اكتبوا اسم المنطقة'],
    phS:      ['e.g. 613', 'مثال: ٦١٣'],
    phJ:      ['e.g. 2', 'مثال: ٢'],
    phH:      ['e.g. 25', 'مثال: ٢٥'],
    phF:      ['e.g. floor 3, flat 2', 'مثال: دور ٣ شقة ٢'],
    /* combobox */
    cbNone:   ['No area matches that. Check the spelling, or carry on and choose the governorate below.',
               'ما في منطقة بهالاسم. تأكدوا من الكتابة، أو كمّلوا واختاروا المحافظة تحت.'],
    cbHint:   ['Type to search, then use the up and down arrows to choose.',
               'اكتبوا عشان تدورون، وبعدين اختاروا بالأسهم فوق وتحت.'],
    cbCount:  ['%n areas match', '%n منطقة مطابقة'],
    cbOne:    ['1 area matches', 'منطقة واحدة مطابقة']
  };
  function t(k) { return T[k][AR ? 1 : 0]; }

  // Composed into address1 for the shipping address.
  var LBL = {
    street: ['Street ', 'شارع '],
    jadda:  ['Jadda ', 'جادة '],
    house:  ['House ', 'منزل '],
    floor:  ['', '']
  };
  function lbl(k) { return LBL[k][AR ? 1 : 0]; }

  var GOV = {
    KU: ['Al Asimah', 'العاصمة', 'KW-KU'],
    HA: ['Hawalli', 'حولي', 'KW-HA'],
    FA: ['Al Farwaniyah', 'الفروانية', 'KW-FA'],
    AH: ['Al Ahmadi', 'الأحمدي', 'KW-AH'],
    JA: ['Al Jahra', 'الجهراء', 'KW-JA'],
    MU: ['Mubarak Al-Kabeer', 'مبارك الكبير', 'KW-MU']
  };
  var GOV_ORDER = ['KU', 'HA', 'FA', 'AH', 'JA', 'MU'];

  /* [English, Arabic, governorate key, [search aliases]].
     167 areas. Sources and per-governorate counts:
     _workspace/06_kuwait-areas.csv and 07_bu-site_area-dropdown-BUILD.md.
     The governorate is DERIVED from this and never asked. An area typed that
     is not in this table does NOT guess - the six-governorate select appears
     and the customer picks. */
  var AREA_BLOB = [
    "Kuwait City~مدينة الكويت~KU~Madinat Al Kuwait|City|Kuwait Town|العاصمة;Sharq~شرق~KU~Sharg|Al Sharq|Al Sharg;Mirqab~المرقاب~KU~Merqab|Murqab|Al Mirqab|Mergab;Qibla~القبلة~KU~Gibla|Al Qibla|Kibla;",
    "Dasman~دسمان~KU~Al Dasman;Sawaber~الصوابر~KU~Al Sawaber|Sawabir;Salhiya~الصالحية~KU~Al Salhiya|Salhia|Salihiya;Watiya~الوطية~KU~Al Watiya|Watya|Wattiya;",
    "Jibla~جبلة~KU~Jiblah|Jebla|Al Jibla;Daiya~الدعية~KU~Al Daiya|Deya|Duaiya|Dayah;Dasma~الدسمة~KU~Al Dasma|Dasmah;Bnaid Al Gar~بنيد القار~KU~Bneid Al Gar|Bnaid Al Qar|Bneid Al-Qar|Benid Al Gar|Bnaid Alqar;",
    "Kaifan~كيفان~KU~Kefan|Kayfan|Kaifan;Mansouriya~المنصورية~KU~Mansuriya|Al Mansouriya|Mansouriyah;Abdullah Al Salem~ضاحية عبدالله السالم~KU~Abdulla Al Salem|Abdullah Al-Salem|Dahiat Abdullah Al Salem|عبدالله السالم;Nuzha~النزهة~KU~Al Nuzha|Nozha|Nuzhah;",
    "Faiha~الفيحاء~KU~Al Faiha|Fayha|Faihaa;Shamiya~الشامية~KU~Al Shamiya|Shamiyah|Shamia;Rawda~الروضة~KU~Al Rawda|Rawdah|Rouda|Roda;Adailiya~العديلية~KU~Al Adailiya|Udailiya|Adeliya|Adailiyah;",
    "Khaldiya~الخالدية~KU~Al Khaldiya|Khaldiyah|Khaldia;Qadsiya~القادسية~KU~Al Qadsiya|Qadisiya|Qadsiyah|Gadsiya;Qortuba~قرطبة~KU~Qurtuba|Cordoba|Gortuba|Qortoba;Surra~السرة~KU~Al Surra|Sura|Sorra|Surrah;",
    "Yarmouk~اليرموك~KU~Al Yarmouk|Yarmuk|Yarmook;Shuwaikh~الشويخ~KU~Shuweikh|Al Shuwaikh|Shuwekh;Shuwaikh Industrial~الشويخ الصناعية~KU~Shuwaikh Industrial Area|Shuweikh Industrial|Shuwaikh Sinaiya;Shuwaikh Port~ميناء الشويخ~KU~Mina Al Shuwaikh|Shuwaikh Port;",
    "Doha~الدوحة~KU~Al Doha|Doha Kuwait|Dohah;Doha Port~ميناء الدوحة~KU~Mina Al Doha;Granada~غرناطة~KU~Gharnata|Gharnatah|Granada;Sulaibikhat~الصليبيخات~KU~Al Sulaibikhat|Sulaibekhat|Slaibikhat|Sulaibikhaat;",
    "North West Sulaibikhat~شمال غرب الصليبيخات~KU~NW Sulaibikhat|Northwest Sulaibikhat;Qairawan~القيروان~KU~Al Qairawan|Kairouan|Qayrawan|Gairawan;Nahdha~النهضة~KU~Al Nahda|Nahda|East Sulaibikhat|Nahdha;Jaber Al Ahmad City~مدينة جابر الأحمد~KU~Jaber Al Ahmad|Madinat Jaber Al Ahmad|Jaber Al-Ahmad;",
    "Al Sour Gardens~حدائق السور~KU~Hadaiq Al Sour|Sour Gardens|Al Soor Gardens;Failaka~فيلكا~KU~Failaka Island|Faylakah|Jazirat Failaka|جزيرة فيلكا;Ouha Island~جزيرة عوهة~KU~Ouha|Awha|عوهة;Miskan Island~جزيرة مسكان~KU~Miskan|مسكان;",
    "Umm Al Namil Island~جزيرة أم النمل~KU~Umm An Namil|Um Al Namel|أم النمل;Kubbar Island~جزيرة كبر~KU~Kubbar|Qubbar|كبر;Umm Al Maradim Island~جزيرة أم المرادم~KU~Umm Al Maradem|أم المرادم;Qaruh Island~جزيرة قاروه~KU~Qaruh|Garouh|قاروه;",
    "Hawalli~حولي~HA~Hawally|Hawalli;Salmiya~السالمية~HA~Salmiyah|Al Salmiya|Salmia|Salmiyya;Rumaithiya~الرميثية~HA~Rumaithiyah|Rmaithiya|Al Rumaithiya|Romaithiya|Rumaithiyya;Bayan~بيان~HA~Bian|Bayan|Baian;",
    "Mishref~مشرف~HA~Mishrif|Meshref|Mushrif|Mishraf;Salwa~سلوى~HA~Salwa|Salwah;Jabriya~الجابرية~HA~Al Jabriya|Jabriyah|Jabria|Jaberiya;Shaab~الشعب~HA~Al Shaab|Shaab|Sha'ab|Shuaab;",
    "Shaab Al Bahri~الشعب البحري~HA~Sea Shaab|Shaab Bahri;Maidan Hawalli~ميدان حولي~HA~Maidan Hawally|Meidan Hawalli;Nugra~النقرة~HA~Al Nugra|Nuqra|Nogra|Nuggra;Hitteen~حطين~HA~Hateen|Hittin|Hitteen;",
    "Zahra~الزهراء~HA~Al Zahra|Zahraa|Zahra;Shuhada~الشهداء~HA~Al Shuhada|Shohada|Shuhadaa;Salam~السلام~HA~Al Salam|Salaam;Siddeeq~الصديق~HA~Al Siddiq|Seddiq|Sideeq|Siddique;",
    "Mubarak Al Abdullah~مبارك العبدالله~HA~Mubarak Al-Abdullah Al-Jaber|West Mishref|غرب مشرف;Bidaa~البدع~HA~Al Bidaa|Bida'a|Bedaa|Bidea;Anjafa~أنجفة~HA~Injafa|Anjafah|Anjfa|انجفة;Ministries Area~منطقة الوزارات~HA~Ministries Zone|Mantaqat Al Wizarat|Wizarat;",
    "Farwaniya~الفروانية~FA~Al Farwaniya|Farwania|Farwaniyah;Khaitan~خيطان~FA~Kheitan|Khaytan|Khitan;New Khaitan~خيطان الجديدة~FA~Khaitan Al Jadida|Kheitan Al Jadeeda;Abraq Khaitan~أبرق خيطان~FA~Abraq Kheitan|Abraq|Abrag Khaitan;",
    "Jleeb Al Shuyoukh~جليب الشيوخ~FA~Jleeb|Jeleeb Al Shuyoukh|Jleeb Al-Shuyoukh|Jaleeb|Jleeb Alshuyoukh;Ardiya~العارضية~FA~Al Ardiya|Ardhiya|Aridhiya|Ardiyah;Ardiya Herafiya~العارضية حرفية~FA~Ardiya Industrial|Ardiya Craft|العارضية الصناعية;Rabiya~الرابية~FA~Al Rabiya|Rabiyah|Rabia;",
    "Andalous~الأندلس~FA~Andalus|Al Andalous|Andalusia;Rehab~الرحاب~FA~Al Rehab|Rihab|Rehaab;Ishbiliya~إشبيلية~FA~Eshbeliya|Ashbiliya|Seville|Ishbilia;Firdous~الفردوس~FA~Ferdous|Al Firdous|Firdaws;",
    "Omariya~العمرية~FA~Al Omariya|Umariya|Omariyah;Riggae~الرقعي~FA~Al Riggai|Rigai|Riqai|Riggai|Rigae;Dajeej~الضجيج~FA~Al Dajeej|Dhajeej|Dajij;Sabah Al Nasser~صباح الناصر~FA~Sabah Al-Nasser|Sabah Alnasser;",
    "Abdullah Al Mubarak~عبدالله المبارك~FA~Abdullah Al-Mubarak Al-Sabah|Abdulla Al Mubarak;West Abdullah Al Mubarak~غرب عبدالله المبارك~FA~West Abdulla Al Mubarak;South Abdullah Al Mubarak~جنوب عبدالله المبارك~FA~South Abdulla Al Mubarak;Rai~الري~FA~Al Rai|Ray|Al-Rai|Rai;",
    "Rai Industrial~الري الصناعية~FA~Al Rai Industrial|Rai Sinaiya;Sulaibiya Industrial~الصليبية الصناعية~FA~Sulaibiya Industrial Area|Slaibiya Industrial;Airport District~منطقة المطار~FA~Kuwait Airport|Matar|Airport;Abbasiya~العباسية~FA~Al Abbasiya|Abbassiya|Abbasiyah;",
    "Hasawi~الحساوي~FA~Al Hasawi|Hassawi|Hasawy;Shadadiya~الشدادية~FA~Al Shadadiya|Shadadiyah|Shadadya;Sabah Al Salem University City~جامعة صباح السالم~FA~Kuwait University Shadadiya|Sabah Al-Salem University|جامعة الكويت;Ahmadi~الأحمدي~AH~Al Ahmadi|Ahmadi;",
    "Fahaheel~الفحيحيل~AH~Al Fahaheel|Fuhayhil|Fahaheel|Fhaiheel;Mangaf~المنقف~AH~Mangef|Manqaf|Al Mangaf|Mangaff;Abu Halifa~أبو حليفة~AH~Abu Hulaifa|Abu Halifah|Bu Halifa|Abu Hlaifa;Fintas~الفنطاس~AH~Al Fintas|Fentas|Fintas;",
    "Mahboula~المهبولة~AH~Al Mahboula|Mahbula|Mahboola;Egaila~العقيلة~AH~Aqaila|Al Egaila|Uqaila|Eqaila|Aqila;Sabahiya~الصباحية~AH~Al Sabahiya|Sabahiyah|Sabahia;South Sabahiya~جنوب الصباحية~AH~Sabahiya South;",
    "Riqqa~الرقة~AH~Al Riqqa|Reqqa|Riga|Riqa;Hadiya~هدية~AH~Hadiyah|Hadya;Dhaher~الظهر~AH~Al Dhaher|Zahar|Daher|Thaher;Wafra~الوفرة~AH~Al Wafra|Wafrah;",
    "Wafra Residential~وفرة السكنية~AH~Wafra Housing|الوفرة السكنية;Wafra Agricultural~الوفرة الزراعية~AH~Wafra Farms|Wafra Zeraiya;Khairan~الخيران~AH~Al Khairan|Khiran|Al-Khiran|Khayran;Khairan City~مدينة الخيران~AH~Madinat Al Khiran|Khiran City;",
    "Sabah Al Ahmad City~مدينة صباح الأحمد~AH~Sabah Al-Ahmad|Sabah Alahmad City;Sabah Al Ahmad Sea City~مدينة صباح الأحمد البحرية~AH~Sea City|Sabah Al Ahmad Marine City|Khiran Sea City;Ali Sabah Al Salem~علي صباح السالم~AH~Ali Sabah Al-Salem|Umm Al Hayman|Umm Alhaiman|Um Alhaiman|ام الهيمان|أم الهيمان;Fahad Al Ahmad~فهد الأحمد~AH~Fahad Al-Ahmad|Fahad Alahmad;",
    "Jaber Al Ali~جابر العلي~AH~Jaber Al-Ali|Jaber Alali;Shuaiba~الشعيبة~AH~Shuaiba Industrial|Al Shuaiba|Shuaibah;Mina Abdullah~ميناء عبدالله~AH~Mina Abdulla|Mina Abdullah;Nuwaiseeb~النويصيب~AH~Al Nuwaiseeb|Nuwaiseb|Nuwaisib;",
    "Zour~الزور~AH~Al Zour|Zoor|Zor;Bnaider~بنيدر~AH~Bneider|Bnaidar|Bnaider;Julaia~الجليعة~AH~Al Julaia'a|Julaiaa|Jleea|Julaia;Magwa~المقوع~AH~Al Magwa|Maqwa|Magwa;",
    "Bar Al Ahmadi~بر الأحمدي~AH~Bar Al Ahmadi;Dubaiya~الضباعية~AH~Al Dubaiya|Dubaiyah;Wara~واره~AH~Warah|Wara;Jahra~الجهراء~JA~Al Jahra|Jahraa|Jahrah;",
    "Old Jahra~الجهراء القديمة~JA~Jahra Old;New Jahra~الجهراء الجديدة~JA~Jahra New;Saad Al Abdullah~سعد العبدالله~JA~Saad Al-Abdullah City|Madinat Saad Al Abdullah|Saad Alabdullah;Naseem~النسيم~JA~Nuseem|Al Naseem|Nasseem|Nassem|Naseem;",
    "Naeem~النعيم~JA~Al Naeem|Naim|Naeem;Qasr~القصر~JA~Al Qasr|Gasr|Qaser;Oyoun~العيون~JA~Al Oyoun|Uyoun|Ayoun|Oyun;Waha~الواحة~JA~Al Waha|Wahah;",
    "Taima~تيماء~JA~Taimaa|Teima|Taima;Sulaibiya~الصليبية~JA~Al Sulaibiya|Slaibiya|Sulaibia|Sulaibiyah;Sulaibiya Residential~الصليبية السكنية~JA~Sulaibiya Housing;Sulaibiya Agricultural~الصليبية الزراعية~JA~Sulaibiya Farms|Sulaibiya Zeraiya;",
    "Amghara~أمغرة~JA~Amghara Industrial|Al Amghara|Amgharah;Kabd~كبد~JA~Kabad|Kabd;Abdali~العبدلي~JA~Al Abdali|Abdaly;Mutlaa~المطلاع~JA~Al Mutlaa|Mutla|Mutlaa;",
    "Salmi~السالمي~JA~Al Salmi|Salmy;Jahra Industrial~الجهراء الصناعية~JA~Jahra Industrial Herafiya|Jahra Sinaiya;Kazma~كاظمة~JA~Kadhma|Kazima|Kathma;Bahra~بحرة~JA~Bahrah|Bahra;",
    "Sheqaya~الشقاية~JA~Al Shaqaya|Shagaya|Shuqaya;Subiya~الصبية~JA~Al Subiya|Sabiya|Subiyah;Rawdatain~الروضتين~JA~Al Rawdatain|Raudhatain|Rawdhatain;Qaisariya~القيصرية~JA~Al Qaisariya|Qaisariyah;",
    "Nawaf Al Ahmad City~مدينة نواف الأحمد~JA~Nawaf Al-Ahmad|Nawaf Alahmad;Silk City~مدينة الحرير~JA~Madinat Al Hareer|Hareer|Al Hareer;Bar Al Jahra~بر الجهراء~JA~Bar Al Jahra;Bubiyan Island~جزيرة بوبيان~JA~Bubiyan|بوبيان;",
    "Warbah Island~جزيرة وربة~JA~Warba|Warbah|وربة;Mubarak Al Kabeer~مبارك الكبير~MU~Mubarak Al-Kabeer|Mubarak Alkabeer;Sabah Al Salem~صباح السالم~MU~Sabah Al-Salem|Sabah Alsalem;Messila~المسيلة~MU~Al Messila|Musaila|Maseela|Misila;",
    "Adan~العدان~MU~Al Adan|Aden|Adaan;Qurain~القرين~MU~Al Qurain|Qurayn|Gurain|Qureen;Qusour~القصور~MU~Al Qusour|Qusoor|Gusour|Qosour;Abu Fatira~أبو فطيرة~MU~Abu Ftaira|Bu Fatira|Abu Futaira|Abu Ftera;",
    "West Abu Fatira~غرب أبو فطيرة~MU~West Abu Ftaira Herafiya|Abu Fatira Craft|غرب ابو فطيرة حرفية;Funaitees~الفنيطيس~MU~Al Fnaitees|Fintees|Funaytis|Fnaitees;Sabhan~صبحان~MU~Subhan|Sabhan Industrial|Subhan Industrial;Masayel~المسايل~MU~Al Masayel|Masayil|Msayel;",
    "Abu Hasaniya~أبو الحصانية~MU~Abu Al Hasaniya|Abu Hasaniyah|Bu Hasaniya|Abu Hassaniya|ابو الحصانية;Wista~الوسطى~MU~Al Wista|Wusta;Thaalab~الثعالب~MU~Al Thaalab|Thaalib|Thaaleb"
  ].join('');

  /* rows ';'  fields '~'  aliases '|'  ->  [en, ar, govKey, [aliases]] */
  var AREAS = (function () {
    var out = [], rows = AREA_BLOB.split(';');
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i]) continue;
      var p = rows[i].split('~');
      out.push([p[0], p[1], p[2], p[3] ? p[3].split('|') : []]);
    }
    return out;
  })();

  /* Arabic-tolerant fold: strips diacritics and tatweel, unifies أإآ -> ا,
     ى -> ي, ة -> ه, drops a leading ال, and removes everything that is not a
     letter or a digit. "الرميثية", "رميثيه" and "الرميثيه" all fold the same. */
  function norm(s) {
    return String(s == null ? '' : s)
      .replace(/[ً-ْـ]/g, '')
      .replace(/[أإآ]/g, 'ا').replace(/ى/g, 'ي').replace(/ة/g, 'ه')
      .replace(/^ال/, '')
      .replace(/[^0-9a-z؀-ۿ]+/gi, '')
      .toLowerCase().trim();
  }

  /* Every area is indexed under its English name, its Arabic name and every
     alias, so an alias resolves the governorate exactly like the real name.
     This is what makes the six-governorate fallback rare. */
  var AREA_IX = {};
  (function () {
    for (var i = 0; i < AREAS.length; i++) {
      var a = AREAS[i];
      AREA_IX[norm(a[0])] = a;
      AREA_IX[norm(a[1])] = a;
      var al = a[3] || [];
      for (var j = 0; j < al.length; j++) {
        var k = norm(al[j]);
        if (k && !AREA_IX[k]) AREA_IX[k] = a;
      }
    }
  })();
  function findArea(v) { return AREA_IX[norm(v)] || null; }

  function label(a) { return AR ? a[1] : a[0]; }

  /* Ranked search over name + alias, both languages at once.
       0 exact name      1 name starts with      2 alias starts with
       3 name contains   4 alias contains
     Ties break on the shorter label, so "Rai" beats "Rai Industrial". */
  function search(q) {
    var n = norm(q);
    if (!n) {
      return AREAS.slice(0);
    }
    var hits = [];
    for (var i = 0; i < AREAS.length; i++) {
      var a = AREAS[i];
      var en = norm(a[0]), ar = norm(a[1]);
      var rank = -1;
      if (en === n || ar === n) rank = 0;
      else if (en.indexOf(n) === 0 || ar.indexOf(n) === 0) rank = 1;
      else if (en.indexOf(n) > 0 || ar.indexOf(n) > 0) rank = 3;
      var al = a[3] || [];
      for (var j = 0; j < al.length && rank !== 0; j++) {
        var k = norm(al[j]);
        if (!k) continue;
        if (k === n) { rank = 0; break; }
        if (k.indexOf(n) === 0) { if (rank < 0 || rank > 2) rank = 2; }
        else if (k.indexOf(n) > 0) { if (rank < 0) rank = 4; }
      }
      if (rank >= 0) hits.push([rank, label(a).length, i, a]);
    }
    hits.sort(function (x, y) { return x[0] - y[0] || x[1] - y[1] || x[2] - y[2]; });
    var out = [];
    for (var m = 0; m < hits.length; m++) out.push(hits[m][3]);
    return out;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function val(el) { return el ? String(el.value || '').replace(/\s+/g, ' ').trim() : ''; }

  /* ---- the customer's saved address book, handed over by Liquid ---- */
  var JOINER = '⁠';   // Shopify's reserved delimiter inside address2 (KW: Block)
  function book() {
    var el = document.getElementById('fl-addrbook');
    if (!el) return [];
    var raw;
    try { raw = JSON.parse(el.getAttribute('data-addresses') || '[]') || []; }
    catch (e) { return []; }
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var a = raw[i] || {};
      var two = String(a.address2 || '');
      var ix = two.indexOf(JOINER);
      a.line2 = ix > -1 ? two.slice(0, ix) : two;
      a.block = ix > -1 ? two.slice(ix + 1) : '';
      out.push(a);
    }
    return out;
  }
  function bookLine(a) {
    var bits = [];
    if (a.address1) bits.push(a.address1);
    if (a.block) bits.push((AR ? 'قطعة ' : 'Block ') + a.block);
    if (a.city) bits.push(a.city);
    if (a.province) bits.push(a.province);
    return bits.join(' · ');
  }

  /* ---- state published for otf-address-skip.js ---- */
  var STATE = { mode: 'none', ready: false, address: null, attrs: {} };
  window.OTF_ADDR = STATE;

  var CALL_LINE = ['Call recipient — address to be taken by phone',
                   'يُتصل بالمستلم — العنوان يؤخذ هاتفياً'][AR ? 1 : 0];
  var CALL_CITY = ['To be confirmed', 'يُحدَّد لاحقاً'][AR ? 1 : 0];

  var UID = 0;

  function styles() {
    if (document.getElementById('otf-addr-css')) return;
    var s = document.createElement('style');
    s.id = 'otf-addr-css';
    s.textContent =
      /* display:flex/grid below would beat the UA's [hidden]{display:none}, which is
         how the governorate fallback select rendered on top of a known area in the
         first preview. Everything this file hides is hidden by the attribute. */
      '[data-otfaddr][hidden],.otf-addr-grid[hidden],.otf-addr-book[hidden],' +
      '.otf-addr-f[hidden],.otf-addr-gov[hidden],[data-af-sub][hidden],' +
      '.otf-cb-lb[hidden],.otf-cb-empty[hidden]{display:none !important}' +
      '.otf-addr-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-top:8px}' +
      '.otf-addr-grid .wide{grid-column:1 / -1}' +
      '.otf-addr-f{display:flex;flex-direction:column;gap:4px}' +
      '.otf-addr-f>span{font-size:12px;opacity:.72}' +
      '.otf-addr-gov{margin-top:8px;font-size:13px;opacity:.8}' +
      '.otf-addr-gov b{font-weight:600;opacity:1}' +
      '.otf-addr-book{display:flex;flex-direction:column;gap:7px;margin-top:8px}' +
      '.otf-addr-pick{width:100%;text-align:start;padding:11px 13px;border-radius:13px;' +
      'border:1.5px solid currentColor;background:transparent;color:inherit;font:inherit;' +
      'opacity:.62;cursor:pointer;line-height:1.35}' +
      '.otf-addr-pick.on{opacity:1;border-width:2px}' +
      '.otf-addr-sel{width:100%;margin-top:6px}' +
      /* --- the area combobox. No new colours: everything is currentColor,
         inherit or transparent, exactly like .otf-addr-pick above. The list is
         INLINE, not floated, so it needs no opaque background and cannot land
         off-screen behind the iOS keyboard. --- */
      '.otf-cb{position:relative;display:flex;flex-direction:column;gap:6px}' +
      '.otf-cb-lb{list-style:none;margin:2px 0 0;padding:4px;max-height:228px;' +
      'overflow-y:auto;-webkit-overflow-scrolling:touch;border:1.5px solid currentColor;' +
      'border-radius:13px;display:block}' +
      '.otf-cb-op{padding:10px 12px;border-radius:9px;cursor:pointer;line-height:1.3;' +
      'font:inherit;color:inherit;opacity:.72;text-align:start;' +
      'display:flex;justify-content:space-between;gap:10px;align-items:baseline}' +
      '.otf-cb-op .otf-cb-g{font-size:11px;opacity:.6;white-space:nowrap}' +
      '.otf-cb-op[aria-selected="true"]{opacity:1;outline:2px solid currentColor;' +
      'outline-offset:-2px}' +
      '.otf-cb-empty{font-size:12px;opacity:.75;margin:2px 0 0;line-height:1.35}' +
      '.otf-cb-in:focus-visible{outline:2px solid currentColor;outline-offset:2px}' +
      '.otf-cb-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}' +
      '@media (prefers-reduced-motion:reduce){.otf-cb-lb{scroll-behavior:auto}}';
    document.head.appendChild(s);
  }

  function fieldHTML(key, label, ph, wide, mode) {
    return '<label class="otf-addr-f' + (wide ? ' wide' : '') + '">' +
      '<span>' + esc(label) + '</span>' +
      '<input class="fluid-ofield" data-af="' + key + '" type="text"' +
      (mode ? ' inputmode="' + mode + '"' : '') +
      ' autocomplete="off" placeholder="' + esc(ph) + '"></label>';
  }

  /* The Area field. A real ARIA 1.2 combobox: the input owns the listbox
     through aria-controls, announces the highlighted row through
     aria-activedescendant, and the rows are role="option". aria-live carries
     the result count so a screen-reader user hears the list change. */
  function comboHTML(uid) {
    var lbId = 'otf-cb-lb-' + uid, laId = 'otf-cb-la-' + uid, hintId = 'otf-cb-h-' + uid;
    return '<div class="otf-addr-f wide">' +
      '<span id="' + laId + '">' + esc(t('area')) + '</span>' +
      '<div class="otf-cb">' +
        '<input class="fluid-ofield otf-cb-in" data-af="area" type="text"' +
          ' role="combobox" aria-expanded="false" aria-controls="' + lbId + '"' +
          ' aria-autocomplete="list" aria-labelledby="' + laId + '"' +
          ' aria-describedby="' + hintId + '"' +
          ' autocomplete="off" autocorrect="off" autocapitalize="off"' +
          ' spellcheck="false" placeholder="' + esc(t('phL')) + '">' +
        '<span class="otf-cb-sr" id="' + hintId + '">' + esc(t('cbHint')) + '</span>' +
        '<ul class="otf-cb-lb" id="' + lbId + '" role="listbox"' +
          ' aria-labelledby="' + laId + '" hidden></ul>' +
        '<p class="otf-cb-empty" data-cb-empty hidden></p>' +
        '<span class="otf-cb-sr" data-cb-live role="status" aria-live="polite"></span>' +
      '</div>' +
    '</div>';
  }

  function sectionHTML(uid) {
    var h = '<section class="fluid-ogrp" data-otfaddr hidden>';
    h += '<h3>' + esc(t('head')) + '</h3>';
    h += '<p class="fluid-onote" data-af-sub></p>';
    h += '<div class="otf-addr-book" data-af-book hidden></div>';
    h += '<div class="otf-addr-grid" data-af-grid>';
    h += comboHTML(uid);
    h += fieldHTML('street', t('street'), t('phS'), false);
    h += fieldHTML('house', t('house'), t('phH'), false);
    h += fieldHTML('jadda', t('jadda'), t('phJ'), false);
    h += fieldHTML('floor', t('floor'), t('phF'), false);
    h += '</div>';
    h += '<p class="otf-addr-gov" data-af-gov hidden></p>';
    h += '<label class="otf-addr-f otf-addr-sel" data-af-govwrap hidden><span>' +
         esc(t('govpick')) + '</span><select class="fluid-ofield" data-af="gov">' +
         '<option value=""></option>';
    for (var i = 0; i < GOV_ORDER.length; i++) {
      var g = GOV[GOV_ORDER[i]];
      h += '<option value="' + GOV_ORDER[i] + '">' + esc(AR ? g[1] : g[0]) + '</option>';
    }
    h += '</select></label>';
    h += '<p class="fluid-onote" data-af-note>' + esc(t('note')) + '</p>';
    h += '<p class="fluid-oerr" data-e="otfaddr">' + esc(t('err')) + '</p>';
    h += '</section>';
    return h;
  }

  /* ---- the combobox controller ---- */
  function combobox(sec, uid, onPick) {
    var input = sec.querySelector('.otf-cb-in');
    var list  = sec.querySelector('.otf-cb-lb');
    var empty = sec.querySelector('[data-cb-empty]');
    var live  = sec.querySelector('[data-cb-live]');
    var rows = [], active = -1, open = false;

    function optId(i) { return 'otf-cb-o-' + uid + '-' + i; }

    function announce(n) {
      if (!live) return;
      live.textContent = n === 1 ? t('cbOne') : t('cbCount').replace('%n', String(n));
    }

    function paint(q) {
      rows = search(q);
      var h = '';
      for (var i = 0; i < rows.length; i++) {
        var a = rows[i];
        h += '<li class="otf-cb-op" role="option" id="' + optId(i) + '"' +
             ' aria-selected="false" data-cb-i="' + i + '">' +
             '<span>' + esc(label(a)) + '</span>' +
             '<span class="otf-cb-g">' + esc(AR ? GOV[a[2]][1] : GOV[a[2]][0]) + '</span>' +
             '</li>';
      }
      list.innerHTML = h;
      active = -1;
      input.removeAttribute('aria-activedescendant');
      var typed = String(input.value || '').trim();
      // "nothing matches" is only worth saying once the customer has typed.
      if (!rows.length && typed) {
        empty.textContent = t('cbNone');
        empty.hidden = false;
      } else {
        empty.hidden = true;
      }
      announce(rows.length);
    }

    function show(q) {
      paint(q === undefined ? input.value : q);
      if (!rows.length) { hide(true); return; }
      list.hidden = false;
      open = true;
      input.setAttribute('aria-expanded', 'true');
    }

    function hide(keepEmpty) {
      list.hidden = true;
      open = false;
      active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      if (!keepEmpty) empty.hidden = true;
    }

    function highlight(i) {
      var opts = list.children;
      if (active > -1 && opts[active]) opts[active].setAttribute('aria-selected', 'false');
      active = i;
      if (active < 0 || !opts[active]) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      opts[active].setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', optId(active));
      if (opts[active].scrollIntoView) opts[active].scrollIntoView({ block: 'nearest' });
    }

    function choose(i) {
      if (i < 0 || !rows[i]) return;
      input.value = label(rows[i]);
      hide();
      empty.hidden = true;
      if (onPick) onPick();
    }

    input.addEventListener('input', function () { show(); });
    input.addEventListener('focus', function () { if (!open) show(); });

    input.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowDown' || k === 'Down') {
        e.preventDefault();
        if (!open) { show(); if (rows.length) highlight(0); return; }
        highlight(active + 1 >= rows.length ? 0 : active + 1);
      } else if (k === 'ArrowUp' || k === 'Up') {
        e.preventDefault();
        if (!open) { show(); if (rows.length) highlight(rows.length - 1); return; }
        highlight(active - 1 < 0 ? rows.length - 1 : active - 1);
      } else if (k === 'Home' && open) {
        e.preventDefault(); highlight(0);
      } else if (k === 'End' && open) {
        e.preventDefault(); highlight(rows.length - 1);
      } else if (k === 'Enter') {
        // Only swallow Enter when it is actually choosing something, so a
        // customer who has typed a name we do not know can still submit.
        if (open && active > -1) { e.preventDefault(); e.stopPropagation(); choose(active); }
        else if (open) { hide(); }
      } else if (k === 'Escape' || k === 'Esc') {
        if (open) { e.preventDefault(); e.stopPropagation(); hide(); }
      } else if (k === 'Tab') {
        hide();
      }
    });

    /* pointerdown, not click: on iOS Safari a click on the list fires AFTER
       the input has blurred and the list has closed, so the tap lands on
       nothing. Taking it on pointerdown and preventing the default stops the
       blur entirely. mousedown is the fallback for anything without pointer
       events. */
    var PD = ('PointerEvent' in window) ? 'pointerdown' : 'mousedown';
    list.addEventListener(PD, function (e) {
      var li = e.target.closest ? e.target.closest('[data-cb-i]') : null;
      if (!li) return;
      e.preventDefault();
      choose(parseInt(li.getAttribute('data-cb-i'), 10));
      if (input.focus) input.focus();
    });

    input.addEventListener('blur', function () {
      // the pointerdown handler above already prevented blur for a real tap,
      // so anything reaching here is a genuine move away from the field.
      setTimeout(function () { if (document.activeElement !== input) hide(); }, 0);
    });

    return { hide: hide, input: input };
  }

  /* ---- per-sheet controller ---- */
  function attach(sheet) {
    if (sheet.__otfAddr) return;
    sheet.__otfAddr = true;
    styles();

    var giftSw = sheet.querySelector('[data-sw="gift"]');
    var callSw = sheet.querySelector('[data-sw="call"]');
    var giftSec = giftSw ? giftSw.closest('section.fluid-ogrp') : null;
    if (!giftSec) return;

    var uid = ++UID;
    var holder = document.createElement('div');
    holder.innerHTML = sectionHTML(uid);
    var sec = holder.firstChild;
    giftSec.parentNode.insertBefore(sec, giftSec.nextSibling);

    var sub = sec.querySelector('[data-af-sub]');
    var grid = sec.querySelector('[data-af-grid]');
    var bookBox = sec.querySelector('[data-af-book]');
    var govLine = sec.querySelector('[data-af-gov]');
    var govWrap = sec.querySelector('[data-af-govwrap]');
    var govSel = sec.querySelector('[data-af="gov"]');
    var note = sec.querySelector('[data-af-note]');
    var errEl = sec.querySelector('[data-e="otfaddr"]');
    var saved = book();
    var chosen = -1;   // -1 = type a new address, >=0 = index in the book

    var cb = combobox(sec, uid, function () { recalc(); });

    function f(k) { return sec.querySelector('[data-af="' + k + '"]'); }
    function isOn(sw) { return !!(sw && sw.classList.contains('on')); }

    function paintBook() {
      if (!saved.length || isOn(giftSw)) { bookBox.hidden = true; bookBox.innerHTML = ''; return; }
      var h = '';
      for (var i = 0; i < saved.length; i++) {
        h += '<button class="otf-addr-pick' + (chosen === i ? ' on' : '') +
             '" type="button" data-af-pick="' + i + '">' + esc(bookLine(saved[i])) + '</button>';
      }
      h += '<button class="otf-addr-pick' + (chosen === -1 ? ' on' : '') +
           '" type="button" data-af-pick="-1">' + esc(t('other')) + '</button>';
      bookBox.innerHTML = h;
      bookBox.hidden = false;
    }

    function paintGov() {
      var a = findArea(val(f('area')));
      if (a) {
        govLine.innerHTML = esc(t('gov')) + ': <b>' + esc(AR ? GOV[a[2]][1] : GOV[a[2]][0]) + '</b>';
        govLine.hidden = false;
        govWrap.hidden = true;
      } else {
        govLine.hidden = true;
        govWrap.hidden = !val(f('area'));
      }
    }

    function governorate() {
      var a = findArea(val(f('area')));
      if (a) return a[2];
      var g = govSel ? String(govSel.value || '') : '';
      return GOV[g] ? g : '';
    }

    /* Kuwait streets and houses are numbers, so the composed line reads
       "Street 613, House 25". When the customer types a NAME instead
       ("Qatar Street", "شارع قطر") the label would repeat itself, so it is only
       added in front of a value that starts with a digit. */
    function part(key) {
      var v = val(f(key));
      if (!v) return '';
      return /^[0-9٠-٩]/.test(v) ? lbl(key) + v : v;
    }
    function compose() {
      var bits = [];
      if (part('street')) bits.push(part('street'));
      if (part('jadda')) bits.push(part('jadda'));
      if (part('house')) bits.push(part('house'));
      if (val(f('floor'))) bits.push(val(f('floor')));
      return bits.join(', ');
    }

    function recalc() {
      var gift = isOn(giftSw);
      var call = gift && isOn(callSw);

      sec.hidden = false;
      sub.textContent = gift ? t('theirs') : t('yours');
      sub.hidden = call;

      if (call) {
        cb.hide();
        grid.hidden = true; bookBox.hidden = true; govLine.hidden = true;
        govWrap.hidden = true; note.textContent = t('callnote');
        errEl.classList.remove('on');
        STATE.mode = 'call'; STATE.ready = true;
        STATE.address = {
          address1: CALL_LINE, city: CALL_CITY,
          provinceCode: GOV.HA[2], zip: ''
        };
        STATE.attrs = { 'Delivery address': CALL_LINE };
        return;
      }

      note.textContent = t('note');
      paintBook();

      if (!gift && chosen >= 0 && saved[chosen]) {
        cb.hide();
        grid.hidden = true; govLine.hidden = true; govWrap.hidden = true;
        errEl.classList.remove('on');
        var s = saved[chosen];
        STATE.mode = 'saved'; STATE.ready = true;
        STATE.address = {
          address1: s.address1 || '', city: s.city || '',
          provinceCode: s.provinceCode || '', zip: s.zip || ''
        };
        STATE.attrs = { 'Delivery address': bookLine(s) };
        return;
      }

      grid.hidden = false;
      paintGov();
      var typed = val(f('area'));
      var known = findArea(typed);
      // the canonical spelling is what goes on the order, so "nuseem" is
      // filed as "Naseem" and the packing slips stay consistent.
      var area = known ? label(known) : typed;
      var street = val(f('street')), house = val(f('house'));
      var gov = governorate();
      var ok = !!(area && street && house && gov);
      STATE.mode = 'fields';
      STATE.ready = ok;
      STATE.address = ok ? {
        address1: compose(), city: area,
        provinceCode: GOV[gov][2], zip: ''
      } : null;
      var attrs = {};
      if (area) attrs['Delivery area'] = area;
      if (street) attrs['Delivery street'] = street;
      if (val(f('jadda'))) attrs['Delivery jadda'] = val(f('jadda'));
      if (house) attrs['Delivery house'] = house;
      if (val(f('floor'))) attrs['Delivery floor'] = val(f('floor'));
      if (gov) attrs['Delivery governorate'] = GOV[gov][0];
      if (ok) attrs['Delivery address'] = compose() + ', ' + area;
      STATE.attrs = attrs;
      if (ok) errEl.classList.remove('on');
    }

    sec.addEventListener('input', recalc);
    sec.addEventListener('change', recalc);
    sec.addEventListener('click', function (e) {
      var p = e.target.closest ? e.target.closest('[data-af-pick]') : null;
      if (!p) return;
      chosen = parseInt(p.getAttribute('data-af-pick'), 10);
      recalc();
    });
    // the sheet's own switches repaint on the next tick
    sheet.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-sw]')) setTimeout(recalc, 0);
    });

    sheet.__otfAddrCheck = function () {
      recalc();
      if (STATE.ready) return true;
      errEl.classList.add('on');
      errEl.scrollIntoView({ block: 'center' });
      return false;
    };
    recalc();
  }

  /* ---- the Continue guard, capture phase so it runs before the sheet's ---- */
  document.addEventListener('click', function (e) {
    var go = e.target.closest ? e.target.closest('.fluid-ogo') : null;
    if (!go || go.disabled) return;
    var sheet = go.closest('.fluid-ord');
    if (!sheet || !sheet.__otfAddrCheck) return;
    if (!sheet.__otfAddrCheck()) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  /* ---- merge our attributes into the sheet's own cart update ---- */
  var realFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (init && String(init.method || 'GET').toUpperCase() === 'POST' &&
          /\/cart\/update\.js(?:\?|$)/.test(url) && typeof init.body === 'string') {
        var body = JSON.parse(init.body);
        if (body && body.attributes && typeof body.attributes.Gift === 'string') {
          var add = STATE.attrs || {};
          for (var k in add) if (Object.prototype.hasOwnProperty.call(add, k)) body.attributes[k] = add[k];
          // clear anything a previous pass left behind
          var keys = ['Delivery area', 'Delivery block', 'Delivery street', 'Delivery jadda',
                      'Delivery house', 'Delivery floor', 'Delivery governorate', 'Delivery address'];
          for (var i = 0; i < keys.length; i++) {
            if (!(keys[i] in add)) body.attributes[keys[i]] = '';
          }
          var next = {};
          for (var p in init) if (Object.prototype.hasOwnProperty.call(init, p)) next[p] = init[p];
          next.body = JSON.stringify(body);
          init = next;
        }
      }
    } catch (err) { /* leave the request exactly as it was */ }
    return realFetch.call(this, input, init);
  };

  /* ---- find the sheet whenever it is opened ---- */
  function scan() {
    var s = document.querySelector('.fluid-ord [data-sw="gift"]');
    if (s) attach(s.closest('.fluid-ord'));
  }
  if (window.MutationObserver) {
    new MutationObserver(function () { scan(); })
      .observe(document.body || document.documentElement, { childList: true, subtree: false });
  }
  document.addEventListener('click', function () { setTimeout(scan, 60); }, true);
  scan();

  /* ---- exposed for verification from the console ---- */
  window.OTF_AREAS = {
    count: AREAS.length,
    keys: (function () { var n = 0; for (var k in AREA_IX) n++; return n; })(),
    find: findArea,
    search: function (q) { var r = search(q), o = []; for (var i = 0; i < r.length; i++) o.push(label(r[i])); return o; }
  };
})();
